/**
 * reportCoverTokens — K5 "Phase D" cover-geometry token spine + the
 * theme-derived DARK-SURFACE contract for the dark cover / dark feature-page
 * rhythm.
 *
 * WHY THIS EXISTS
 * ---------------
 * The seven cover layouts each hand-coded their page geometry as scattered
 * literals — the `min-h-[500px]` / `min-h-[260px]` hero floors, the
 * `minHeight: 520` masthead/data-snapshot/boundary-plate root floor, the
 * `grid grid-cols-2 sm:grid-cols-4` metadata/KPI grid, the bottom-right
 * disclaimer footer block, the hero aspect ratio. A change to one drifted the
 * others (the same "fix one cover, regress another" defect class
 * `reportLayoutTokens.ts` exists to kill for page margins). This module is the
 * ONE source of truth for those cover dimensions so every cover variant reads
 * the same numbers.
 *
 * BYTE-STABILITY
 * --------------
 * The geometry constants below are the CURRENT production values
 * (`COVER_MIN_HEIGHT_PX = 520`, hero floors 500 / 260, etc.). Routing the
 * existing covers through them is a pure refactor — no rendered output changes.
 *
 * THE DARK SURFACE (the K5 addition)
 * ----------------------------------
 * The redesign reference cover is a near-black instrument panel with an amber
 * accent. We do NOT adopt that near-black/amber skin — we DERIVE a dark surface
 * from OUR active theme tokens via `resolveCoverDarkSurface(theme)`:
 *   - the dark page surface is the theme's clamped INK (`roleColor(theme,
 *     'ink')`), which is already a near-black derived from `headingColor`;
 *   - the on-dark text ramp (text / muted / faint / hairline) is derived by
 *     mixing the document-white base back IN over that ink, the mirror of the
 *     light-page neutral ramp in `reportRoleColors.ts`;
 *   - the accent stays the theme's own `primaryColor` (`roleColor(theme,
 *     'accent')`), so a journal/institutional/monograph preset keeps its own
 *     hue on the dark surface instead of the redesign's amber.
 * No raw hex is introduced for the surface — every value is theme-token
 * derived (the `ci:design-tokens` sentinel and the WCAG legibility test both
 * assert this).
 *
 * GATED BRAND AFFORDANCES
 * -----------------------
 * Crop marks + datum strips are OPTIONAL print-safe SVG affordances, OFF by
 * default. They turn on only via the theme's `coverBrandAffordances` opt-in
 * (see {@link coverBrandAffordancesEnabled}). The dark-cover layout itself is
 * additive/opt-in: it is never a preset default and never resolves unless a
 * user/template selects `'dark-instrument'`.
 *
 * PURE / synchronous / null-safe — safe to inline into render bodies.
 */
import type { ReportTheme } from '../types/reportTheme';
import { roleColor } from './reportRoleColors';

/* ── Cover page geometry (extracted literals) ──────────────────────────── */

/**
 * The cover root MIN-HEIGHT in px — the floor every journal-aesthetic cover
 * (masthead / data-snapshot / boundary-plate / dark-instrument) lays out
 * against so the page-1 content fills the A4 frame. This is the
 * `minHeight: 520` literal that was hand-copied across those covers.
 */
export const COVER_MIN_HEIGHT_PX = 520;

/**
 * Hero-image cover root floors (`min-h-[500px]` / `min-h-[260px]`). The
 * full-page floor fills the A4 frame; the compact floor renders the hero as a
 * shorter banner so more body content sits above the fold. Exposed as Tailwind
 * arbitrary-value classes so HeroImageCover keeps emitting the SAME classes the
 * `HeroImageCover.layout.test.tsx` geometry oracle pins.
 */
export const COVER_HERO_FULL_PAGE_MIN_H_CLASS = 'min-h-[500px]';
export const COVER_HERO_COMPACT_MIN_H_CLASS = 'min-h-[260px]';

/** The hero overlay/figure aspect ratio (the `aspect-[2/1]` boundary-overlay
 *  frame). One token so the hero + boundary covers can't drift. */
export const COVER_HERO_ASPECT_CLASS = 'aspect-[2/1]';

/**
 * The metadata / KPI rail grid template. The redesign's bottom KPI rail and the
 * data-snapshot four-figure grid both lay four cells across at wide widths,
 * collapsing to two on narrow. ONE class so the cover grids stay in lockstep.
 */
export const COVER_METADATA_GRID_CLASS = 'grid grid-cols-2 sm:grid-cols-4 gap-0';

/** The data-dense KPI chip grid (1 → 2 → 3 columns). */
export const COVER_KPI_CHIP_GRID_CLASS = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4';

/**
 * The bottom-right confidential disclaimer footer block. Every cover anchors
 * the disclaimer here (the `journalCovers.test.tsx` parity oracle asserts
 * `absolute bottom-0 right-0 text-right text-[8px]` on each cover), so the
 * footer positioning lives in ONE token.
 */
export const COVER_DISCLAIMER_FOOTER_CLASS =
  'absolute bottom-0 right-0 text-right text-[8px] leading-relaxed max-w-md';

/* ── Datum strip + crop-mark geometry (the gated brand affordances) ─────── */

/** Datum-strip dash pattern (the dotted engineering ruler), in CSS user units.
 *  Print-safe (an SVG `stroke-dasharray`, no raster). */
export const COVER_DATUM_DASH_ARRAY = '2 4';
/** Datum-strip stroke width, px. Hairline so it prints crisp. */
export const COVER_DATUM_STROKE_WIDTH = 1;

/** Crop-mark arm length + inset from the page edge, px. A registration mark is
 *  an L of two strokes; the inset keeps it inside the printable frame. */
export const COVER_CROP_MARK_LENGTH_PX = 14;
export const COVER_CROP_MARK_INSET_PX = 0;
/** Crop-mark stroke width, px (hairline). */
export const COVER_CROP_MARK_STROKE_WIDTH = 1;

/* ── Theme-derived DARK SURFACE contract ───────────────────────────────── */

/**
 * The on-dark neutral / accent ramp for the dark cover + dark feature pages,
 * DERIVED FROM the active theme (no fixed skin). Mirror of the light-page
 * `roleColor` neutral ramp: there, ink is mixed toward white for muted/faint;
 * here, the document-white base is mixed back IN over the dark surface for the
 * on-dark text/muted/faint/hairline.
 */
export interface CoverDarkSurface {
  /** The dark page surface (the theme's clamped near-black ink). */
  surface: string;
  /** A faintly raised panel on the surface (instrument-card background). */
  panel: string;
  /** Primary on-dark text (near-white, tinted by the surface). */
  text: string;
  /** Secondary on-dark text (labels). */
  muted: string;
  /** Faintest on-dark meta text. */
  faint: string;
  /** On-dark hairline rule / datum strip. */
  hair: string;
  /** The brand accent — the theme's OWN primary, unchanged on the dark surface. */
  accent: string;
}

/** The document-white base the on-dark ramp is mixed from (mirrors
 *  `reportRoleColors.DOC_BG`). */
const ON_DARK_BASE = '#ffffff';

/** Parse `#rgb`/`#rrggbb` → [r,g,b] (0–255) or null for non-hex. */
function parseHexRgb(value: string): [number, number, number] | null {
  const m = value.trim().replace(/^#/, '');
  const e = m.length === 3 ? m.split('').map((c) => c + c).join('') : m;
  if (!/^[0-9a-f]{6}$/i.test(e)) return null;
  return [parseInt(e.slice(0, 2), 16), parseInt(e.slice(2, 4), 16), parseInt(e.slice(4, 6), 16)];
}

/** Mix `a` into `b` by `weight` of `a` (0..1) in sRGB; `fallback` on bad input. */
function mixHex(a: string, b: string, weight: number, fallback: string): string {
  const pa = parseHexRgb(a);
  const pb = parseHexRgb(b);
  if (!pa || !pb) return fallback;
  const w = Math.max(0, Math.min(1, weight));
  const to2 = (v: number) => Math.round(v).toString(16).padStart(2, '0');
  const ch = (i: 0 | 1 | 2) => to2(pa[i] * w + pb[i] * (1 - w));
  return `#${ch(0)}${ch(1)}${ch(2)}`;
}

/**
 * Resolve the dark-surface ramp from the active theme. Every value is derived
 * from `roleColor(theme, …)` — the surface is the clamped ink, the on-dark text
 * ramp mixes the document-white base into that ink at increasing weights, and
 * the accent is the theme's own primary. PURE + null-safe (roleColor is total
 * for any partial theme, so an absent/blank token degrades to DEFAULT_THEME).
 *
 * The mix weights mirror the light-page ramp's spacing (muted ≈ 0.66 of the way
 * to the base, faint ≈ 0.82) but inverted (white into ink, not ink into white),
 * keeping the on-dark neutral ramp legible on the dark surface in every preset.
 */
export function resolveCoverDarkSurface(
  theme: Partial<ReportTheme> | null | undefined,
): CoverDarkSurface {
  // The surface IS the clamped ink (a near-black derived from headingColor).
  const surface = roleColor(theme, 'ink');
  const accent = roleColor(theme, 'accent');
  return {
    surface,
    // A faintly raised panel — a touch of white mixed into the surface.
    panel: mixHex(ON_DARK_BASE, surface, 0.07, '#1a1f25'),
    // Near-white primary text, slightly tinted by the surface so it reads as
    // part of the dark instrument, not a raw white.
    text: mixHex(ON_DARK_BASE, surface, 0.92, '#f2f3f4'),
    muted: mixHex(ON_DARK_BASE, surface, 0.66, '#aab0b8'),
    faint: mixHex(ON_DARK_BASE, surface, 0.46, '#7d848d'),
    hair: mixHex(ON_DARK_BASE, surface, 0.22, '#3a4047'),
    accent,
  };
}

/* ── Gated brand affordances (crop marks / datum strips) ───────────────── */

/**
 * The cover brand-affordance opt-in, read off the theme. ABSENT ⇒ OFF (every
 * legacy/default theme renders no crop marks / datum strips, byte-identical).
 * A theme/template opts a single affordance in by setting the matching flag.
 * Both are print-safe SVG and token-styled.
 */
export interface CoverBrandAffordances {
  /** Corner registration / crop marks at the four page corners. */
  cropMarks?: boolean;
  /** Dotted engineering "datum strip" rulers (the mono coordinate strips). */
  datumStrips?: boolean;
  /** Render the designated interior feature panels on the theme-derived dark
   *  surface for document gravity/rhythm. */
  darkFeatureRhythm?: boolean;
}

/** True when the named affordance is opted IN on the theme (off when absent). */
export function coverBrandAffordancesEnabled(
  theme: Partial<ReportTheme> | null | undefined,
  affordance: keyof CoverBrandAffordances,
): boolean {
  const cfg = theme?.coverBrandAffordances;
  return cfg != null && cfg[affordance] === true;
}
